// app/src/lib/backup/restorer.js — pull the latest S3 snapshot and import into Dexie.
//
// Prompts the user before clobbering local state. A user-dismissed restore stays
// dismissed for 24 hours.

import pako from 'pako';
import { db, resetDb } from '../db.js';
import { deriveKeys, decrypt } from '../crypto.js';
import * as kv from '../idbKv.js';
import { getAccessToken, currentClaims } from '../auth/cognitoClient.js';
import { getConfig } from '../config.js';

const DISMISS_KEY = 'restoreDismissedUntil'; // allow-listed in lint-no-localstorage.

/**
 * @returns {Promise<{ available: boolean, lastModified?: number, bytes?: number, dismissedUntil?: number }>}
 */
export async function checkAndOfferRestore() {
  const dismissedUntil = Number(localStorage.getItem(DISMISS_KEY) || 0);
  if (dismissedUntil > Date.now()) return { available: false, dismissedUntil };

  const meta = await fetchRemoteMeta();
  if (!meta) return { available: false };
  const localLast = Number(localStorage.getItem('lastBackupAt') || 0);
  if (meta.lastModified <= localLast) return { available: false };
  return { available: true, lastModified: meta.lastModified, bytes: meta.bytes };
}

export function dismissRestoreFor24h() {
  localStorage.setItem(DISMISS_KEY, String(Date.now() + 24 * 60 * 60 * 1000));
}

/**
 * Perform the restore. Fully replaces local Dexie state.
 * Throws on decryption failure, schema mismatch, or userId mismatch.
 */
export async function performRestore() {
  const claims = await currentClaims();
  if (!claims) throw new Error('restore: not signed in');
  const userId = claims.sub;

  const url = await getPresignedGet();
  const res = await fetch(url);
  if (!res.ok) throw new Error(`restore: GET failed ${res.status}`);
  const envelope = new Uint8Array(await res.arrayBuffer());

  const { backupKey } = await deriveKeys(userId, kv);
  const aad = new TextEncoder().encode(`kanjo/backup/v2|${userId}`);
  let plain;
  try { plain = await decrypt(backupKey, envelope, aad); }
  catch (e) { throw new Error('restore: decryption failed — wrong key or tampered blob'); }

  const json = new TextDecoder().decode(pako.ungzip(plain));
  const snapshot = JSON.parse(json);
  if (snapshot.userId !== userId) throw new Error('restore: userId mismatch in snapshot');
  if (snapshot.version !== 2) throw new Error(`restore: unsupported version ${snapshot.version}`);

  await resetDb();
  await db.transaction('rw', db.tables, async () => {
    for (const [tableName, rows] of Object.entries(snapshot.tables)) {
      if (!db.tables.find((t) => t.name === tableName)) continue;
      if (!Array.isArray(rows) || rows.length === 0) continue;
      await db.table(tableName).bulkPut(rows);
    }
  });
}

async function fetchRemoteMeta() {
  const cfg = getConfig();
  const at = await getAccessToken();
  if (!at) return null;
  const res = await fetch(`${cfg.apiBaseUrl}/backup/meta`, {
    headers: { authorization: `Bearer ${at}` },
  });
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`restore: meta ${res.status}`);
  return res.json();
}

async function getPresignedGet() {
  const cfg = getConfig();
  const at = await getAccessToken();
  if (!at) throw new Error('restore: no access token');
  const res = await fetch(`${cfg.apiBaseUrl}/backup/presign?op=get`, {
    headers: { authorization: `Bearer ${at}` },
  });
  if (!res.ok) throw new Error(`restore: presign ${res.status}`);
  const body = await res.json();
  return body.url;
}
